import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { userCardStyles } from './styles';
import { Edit, Delete, Replay } from '@mui/icons-material';
import { EmailIcon, PhoneIcon, AddressIcon } from 'components/Shared/Icons/tooltip_icons';
import { User, USER_TYPE_ADMIN } from '../../../types/auth/types';
import { SyntheticEvent, useState } from 'react';
import axiosService from '../../../utils/axios';
import AddOrEditUser from './AddOrEditUser';
import { useNavigate } from 'react-router-dom';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: User;
  showActionButtons: boolean;
  onUserDelete?: () => void;
  onUserUpdate?: () => void;
}

const UserCard = (props: Props) => {
  const { option: user, showActionButtons = true, onUserDelete, onUserUpdate } = props;

  const navigate = useNavigate();

  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>();

  const isUserDeleted = !user.is_active;

  const navigateToUserPage = (event: SyntheticEvent) => {
    // Go to user page
    event.preventDefault();
    event.stopPropagation();
    navigate(`/home/users/${user.id}`);
  };

  const deleteUser = () => {
    axiosService
      .delete(`${process.env.REACT_APP_API_URL}/user/${user.id}/`)
      .then(() => {
        if (onUserDelete) {
          onUserDelete();
        }
      })
      .catch(error => setError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const activateUser = () => {
    axiosService
      .post(`${process.env.REACT_APP_API_URL}/user/${user.id}/activate/`)
      .then(() => {
        if (onUserUpdate) {
          onUserUpdate();
        }
      })
      .catch(error => setError(error))
      .finally(() => setActivateDialogOpen(false));
  };

  const onUserEdit = () => {
    if (onUserUpdate) {
      onUserUpdate();
    }
    setEditUserModalOpen(false);
  };

  const getCardStyles = () => {
    if (isUserDeleted) {
      return { ...userCardStyles.optionCard, ...userCardStyles.deleted };
    }
    return userCardStyles.optionCard;
  };

  return (
    <>
      <Card elevation={1} sx={getCardStyles()} onClick={navigateToUserPage}>
        <CardHeader
          action={
            user.user_type !== USER_TYPE_ADMIN && showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={userCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditUserModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                {!isUserDeleted ? (
                  <Tooltip title="Delete">
                    <IconButton
                      sx={userCardStyles.cardAction}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Activate">
                    <IconButton
                      sx={userCardStyles.cardAction}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setActivateDialogOpen(true);
                      }}
                    >
                      <Replay />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            ) : null
          }
          title={`${user.first_name} ${user.last_name}`}
          subheader={`ID: ${user.id}`}
          sx={userCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={userCardStyles.contactBox}>
            <EmailIcon />
            <Typography color="text" sx={userCardStyles.contactValue}>
              {user.email || '-'}
            </Typography>
          </Box>
          <Box sx={userCardStyles.contactBox}>
            <PhoneIcon />
            <Typography color="text" sx={userCardStyles.contactValue}>
              {user.phone || '-'}
            </Typography>
          </Box>
          <Box sx={userCardStyles.contactBox}>
            <AddressIcon />
            <Typography color="text" sx={userCardStyles.contactValue}>
              {user.address || '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete user{' '}
            <b>
              {user.first_name} {user.last_name}
            </b>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteUser} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={activateDialogOpen} onClose={() => setActivateDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Activate user</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to activate user{' '}
            <b>
              {user.first_name} {user.last_name}
            </b>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={activateUser} autoFocus>
            Activate
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditUser
        user={user}
        onSave={onUserEdit}
        modalTitle="Edit user"
        isModalOpen={editUserModalOpen}
        setModalOpen={setEditUserModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup title={error?.statusText} message={error?.message} onClose={() => setError(null)} />
    </>
  );
};

export default UserCard;
